import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import "./SignContract.css";

const BACKEND_URL = "";

const SignContract = () => {
  const { sigId } = useParams();
  const [sig, setSig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);

  // Fetch the signature request
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/sign/${sigId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => {
        setSig(data);
        setSignerName(data.client_name || "");
        setLoading(false);
      })
      .catch(() => { setError("This contract link is invalid or has expired."); setLoading(false); });
  }, [sigId]);

  // Canvas drawing handlers — works for mouse + touch
  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = getPoint(e);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const point = getPoint(e);
    ctx.strokeStyle = "#0A1628";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPoint.current = point;
  };

  const stopDrawing = () => { drawing.current = false; };

  const clearSignature = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  // Initialize white canvas background
  useEffect(() => {
    if (canvasRef.current) clearSignature();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const isCanvasEmpty = useCallback(() => {
    if (!canvasRef.current) return true;
    const ctx = canvasRef.current.getContext("2d");
    const data = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    for (let i = 0; i < data.length; i += 4) {
      // Look for any non-white pixel
      if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return false;
    }
    return true;
  }, []);

  const submitSignature = async () => {
    if (!signerName.trim()) { alert("Please type your full legal name."); return; }
    if (!attested) { alert("Please check the attestation box to confirm you agree."); return; }
    if (isCanvasEmpty()) { alert("Please draw your signature in the box below."); return; }
    setSubmitting(true);
    try {
      const signature_data = canvasRef.current.toDataURL("image/png");
      const r = await fetch(`${BACKEND_URL}/api/sign/${sigId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_data, signer_name: signerName.trim() })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Signing failed");
      }
      setSuccess(true);
    } catch (e) {
      alert("Signature submission failed: " + e.message);
    }
    setSubmitting(false);
  };

  if (loading) return <div className="sc-page"><div className="sc-loading">Loading agreement…</div></div>;
  if (error) return <div className="sc-page"><div className="sc-error">{error}</div></div>;
  if (!sig) return null;

  const isSigned = sig.status === "signed" || success;

  return (
    <div className="sc-page" data-testid="sign-contract-page">
      <div className="sc-shell">
        {/* Header */}
        <header className="sc-header">
          <div className="sc-brand">
            <div className="sc-brand-mark">MOOVIN FORWARD LLC</div>
            <div className="sc-brand-meta">Texas SOS File #806595070 &middot; moovinforward.org</div>
          </div>
          <a
            href={`${BACKEND_URL}/api/sign/${sigId}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="sc-pdf-btn"
            data-testid="download-pdf-link"
          >
            <i className="fas fa-file-pdf"></i> Download PDF
          </a>
        </header>

        {/* Title + meta */}
        <div className="sc-title-block">
          <div className="sc-eyebrow">Pending Signature</div>
          <h1 className="sc-title">{sig.contract_title || sig.contract_type.replace(/_/g, " ").toUpperCase()}</h1>
          <div className="sc-meta">
            {sig.client_name && <div><strong>To:</strong> {sig.client_name}</div>}
            {sig.client_email && <div><strong>Email:</strong> {sig.client_email}</div>}
            {sig.property_address && <div><strong>Property:</strong> {sig.property_address}</div>}
            <div><strong>Sent by:</strong> {sig.sent_by_name || "Moovin Forward LLC"}</div>
            <div><strong>Sent:</strong> {sig.sent_at ? new Date(sig.sent_at).toLocaleString() : "—"}</div>
            {sig.expires_at && !isSigned && (
              <div><strong>Expires:</strong> {new Date(sig.expires_at).toLocaleDateString()}</div>
            )}
          </div>
        </div>

        {/* Already signed banner */}
        {isSigned && (
          <div className="sc-signed-banner" data-testid="signed-banner">
            <div className="sc-signed-icon">✓</div>
            <div>
              <strong>This agreement has been signed.</strong>
              <p>
                Signed by {sig.signer_name || "Client"} on {sig.signed_at ? new Date(sig.signed_at).toLocaleString() : "—"}.
                Download the full executed PDF using the button above.
              </p>
            </div>
          </div>
        )}

        {/* Contract sections */}
        <div className="sc-contract" data-testid="contract-content">
          {(sig.contract_content || []).map((s, i) => (
            <section key={i} className="sc-section">
              <h2 className="sc-section-heading">{s.heading}</h2>
              <pre className="sc-section-body">{s.body}</pre>
            </section>
          ))}
          {(!sig.contract_content || sig.contract_content.length === 0) && (
            <div className="sc-empty-contract">
              <p>The full contract text could not be loaded. Please download the PDF using the button above, or contact Moovin Forward directly.</p>
              <p><strong>Contact:</strong> (817) 381-8831 &middot; nivoomforward@gmail.com</p>
            </div>
          )}
        </div>

        {/* Signature panel (only if not yet signed) */}
        {!isSigned && (
          <div className="sc-sign-panel" data-testid="signature-panel">
            <h2 className="sc-sign-heading">Sign This Agreement</h2>
            <p className="sc-sign-intro">
              By providing your typed full legal name, drawing your signature, and submitting, you are executing
              this agreement under the federal Electronic Signatures in Global and National Commerce Act (E-SIGN)
              and the Texas Uniform Electronic Transactions Act. Your signature carries the same legal effect as
              a handwritten signature.
            </p>

            <label className="sc-field">
              <span className="sc-field-label">Your Full Legal Name</span>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="As it appears on government identification"
                className="sc-field-input"
                data-testid="signer-name-input"
              />
            </label>

            <label className="sc-field">
              <span className="sc-field-label">Draw Your Signature</span>
              <div className="sc-canvas-wrap">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={180}
                  className="sc-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
                <button onClick={clearSignature} className="sc-clear-btn" data-testid="clear-signature">
                  Clear
                </button>
              </div>
            </label>

            <label className="sc-checkbox-row">
              <input
                type="checkbox"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
                className="sc-checkbox"
                data-testid="attestation-checkbox"
              />
              <span className="sc-checkbox-text">
                I have read and understood the foregoing agreement in its entirety. I have had the opportunity
                to consult independent legal counsel of my choosing. I confirm that I am authorized to sign
                on behalf of the party identified above. I agree to be legally bound by the terms of this
                agreement, including any conspicuous indemnification, limitation of liability, and venue or
                jury-waiver provisions contained herein.
              </span>
            </label>

            <button
              onClick={submitSignature}
              disabled={submitting || !signerName.trim() || !attested}
              className="sc-submit-btn"
              data-testid="submit-signature-btn"
            >
              {submitting ? "Submitting…" : "Sign & Execute Agreement"}
            </button>

            <p className="sc-footnote">
              This signing page is provided by Moovin Forward LLC. For questions before signing, contact
              Shannon Ware at (817) 381-8831 or nivoomforward@gmail.com.
            </p>
          </div>
        )}

        <footer className="sc-footer">
          Moovin Forward LLC &middot; Texas SOS File #806595070 &middot; EIN 41-3203573<br />
          (817) 381-8831 &middot; nivoomforward@gmail.com &middot; moovinforward.org
        </footer>
      </div>
    </div>
  );
};

export default SignContract;
